import React, { useState, useEffect, useMemo } from 'react';
import { Youtube, Loader2, Play, Film } from 'lucide-react';
import { getShortsFeed, getCategories } from '../services/careerService';
import { appendUtm } from '../services/utm';
import { normalizeYouTube } from '../services/youtube';
import { isValidYouTubeId } from '../services/videoValidate';
import Button from '../components/Button';
import SEO from '../components/SEO';
import SmartImage from '../components/SmartImage';
import NewsletterForm from '../components/NewsletterForm';
import { track } from '../services/track';

const LatestShortsPage = () => {
  const [careers, setCareers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  
  const categories = getCategories();

  useEffect(() => {
    const start = performance.now();
    setIsLoading(true);
    getShortsFeed().then(data => {
      const sorted = [...data].sort((a, b) => 
        new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
      );
      setCareers(sorted);
      setIsLoading(false);
      console.debug(`[Perf] Shorts Feed Loaded: ${Math.round(performance.now() - start)}ms`);
      track('shorts_view');
    });
  }, []);

  const filteredCareers = useMemo(() => {
    if (!activeCategory) return careers;
    return careers.filter(c => {
      const cat = categories.find(cat => cat.id === activeCategory);
      return c.category === cat?.label;
    });
  }, [careers, activeCategory, categories]);

  return (
    <div className="min-h-screen bg-brand-bg pb-20">
      <SEO 
        title="Latest Shorts" 
        description="Watch the latest 60-second career guides from FuturePath Teens. Learn about engineering, design, science and more in under a minute." 
      />

      <div className="bg-brand-dark border-b border-brand-border py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-semibold mb-6">
            <Youtube className="w-4 h-4" />
            FRESH CONTENT
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-white mb-4">Latest Shorts</h1>
          <p className="text-brand-grey2 max-w-2xl mx-auto mb-10">
            Discover a whole library of career paths in 60 seconds or less. Fast, fun, and factual.
          </p>
          <div className="max-w-3xl mx-auto">
            <NewsletterForm />
          </div>
        </div>
      </div>

      <div className="sticky top-16 z-30 bg-brand-bg/95 backdrop-blur border-b border-brand-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4 overflow-x-auto pb-1 scrollbar-hide">
            <Button 
              size="sm" 
              variant={!activeCategory ? 'primary' : 'secondary'} 
              onClick={() => setActiveCategory(null)}
              className="whitespace-nowrap"
            >
              All Paths
            </Button>
            {categories.map(cat => (
              <Button 
                key={cat.id}
                size="sm" 
                variant={activeCategory === cat.id ? 'primary' : 'secondary'} 
                onClick={() => {
                  setActiveCategory(cat.id);
                  track('category_filter', { id: cat.id, context: 'shorts' });
                }}
                className="whitespace-nowrap"
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-10 h-10 text-brand-cyan animate-spin" />
          </div>
        ) : filteredCareers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {filteredCareers.map((career) => {
              const { watchUrl, youtubeId } = normalizeYouTube(career.video?.youtubeUrl || career.video?.youtubeId || '');
              const isLive = isValidYouTubeId(youtubeId);
              const finalUrl = watchUrl ? appendUtm(watchUrl) : null;

              return (
                <div key={career.slug} className="group relative bg-brand-light border border-brand-border rounded-2xl overflow-hidden hover:border-brand-cyan transition-all duration-300">
                  <div className="aspect-video relative overflow-hidden">
                    <SmartImage 
                      src={career.images?.card} 
                      alt={career.title} 
                      width={800}
                      height={450}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-brand-bg/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      {isLive ? (
                        <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white">
                          <Play className="w-6 h-6 fill-current ml-1" />
                        </div>
                      ) : (
                        <div className="bg-brand-bg/80 backdrop-blur px-3 py-1.5 rounded-lg border border-brand-border text-xs font-bold text-brand-grey2">
                          COMING SOON
                        </div>
                      )}
                    </div>
                    <div className="absolute bottom-3 left-3">
                       <span className="px-2 py-1 rounded bg-black/60 backdrop-blur-md text-[10px] font-bold text-white uppercase tracking-wider">
                          {career.category}
                       </span>
                    </div>
                  </div>
                  
                  <div className="p-5">
                    <h3 className="font-heading font-semibold text-lg text-brand-white mb-4 line-clamp-1">
                      {career.title}
                    </h3>
                    {isLive && finalUrl ? (
                      <a 
                        href={finalUrl} 
                        target="_blank" 
                        rel="noreferrer"
                        className="block"
                        onClick={() => {
                          track('shorts_open', { slug: career.slug });
                          if (youtubeId) track('youtube_click', { slug: career.slug, youtubeId });
                        }}
                      >
                        <Button fullWidth className="bg-red-600 hover:bg-red-700 text-white border-none">
                          <Youtube className="w-4 h-4 mr-2" />
                          Watch Short
                        </Button>
                      </a>
                    ) : (
                      <Button fullWidth variant="secondary" disabled className="opacity-50 grayscale cursor-not-allowed">
                        <Film className="w-4 h-4 mr-2" />
                        Video Coming Soon
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 bg-brand-light/20 border border-dashed border-brand-border rounded-2xl">
            <h3 className="text-xl font-heading text-brand-white">No videos found for this category</h3>
            <Button variant="ghost" className="mt-4" onClick={() => setActiveCategory(null)}>View All</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LatestShortsPage;
